/**
 * The shapes the learn rules read and write. They are structural twins of the
 * contract's zod schemas (packages/contract/src/learn.ts) without depending on
 * zod, so this package stays free of I/O and the edge, the PWA and the lint can
 * all call the same pure functions on the same data.
 */

export const STEP_KINDS = ["read", "check", "taste", "drill"] as const;
export type StepKind = (typeof STEP_KINDS)[number];

export const DRILL_KINDS = ["aroma", "marker"] as const;
export type DrillKind = (typeof DRILL_KINDS)[number];

export const TASTE_FORMATS = ["single", "horizontal", "vertical", "diagonal"] as const;
export type TasteFormat = (typeof TASTE_FORMATS)[number];

/** One synced record. `value` is validated per kind at the edge and read leniently here. */
export interface ProgressItem {
  readonly key: string;
  /** ISO 8601 UTC instant; the clock last-writer-wins compares. */
  readonly updatedAt: string;
  readonly value: unknown;
}

/** A note attached to a taste step: its id only, never its content. */
export interface StepNote {
  readonly archetype: number;
  readonly noteId: string;
  readonly flightId?: string;
}

/** Value of `step:<lessonId>:<stepId>`. */
export interface StepValue {
  readonly kind: StepKind;
  readonly done: boolean;
  /** First-done, never overwritten, so completion times stay stable across devices. */
  readonly doneAt?: string;
  /** A taste step put off ("no glass tonight") without blocking the lesson. */
  readonly deferredAt?: string;
  readonly notes?: readonly StepNote[];
  /** The device-shuffled reference labels of a drill step: `{ A: "citrus.lime" }`. */
  readonly prep?: Readonly<Record<string, string>>;
}

export interface CheckSitting {
  readonly id: string;
  readonly startedAt: string;
}

/** Value of `check:<checkId>`. `answer` is the authored option index, whatever the on-screen order. */
export interface CheckValue {
  readonly answer: number;
  readonly correct: boolean;
  readonly answeredAt: string;
  readonly attempts: number;
  readonly sitting?: CheckSitting;
}

/** The spaced-repetition library's card, opaque outside `schedule()`. */
export type CardState = Readonly<Record<string, unknown>>;

/** Everything about a drill item's schedule; `kind` belongs to the catalogue, not to the schedule. */
export interface DrillState {
  /** UTC day (`YYYY-MM-DD`) the item is next due. */
  readonly due: string;
  readonly firstAnsweredAt?: string;
  readonly lastAnsweredAt?: string;
  readonly lastCorrect?: boolean;
  /** Correct answers in a row; two or more is "secure" for mastery. */
  readonly streak: number;
  readonly reps: number;
  readonly lapses: number;
  readonly state: CardState;
}

/** Value of `drill:<itemId>`. */
export interface DrillValue extends DrillState {
  readonly kind: DrillKind;
}

/** Value of `level:<levelId>`. */
export interface LevelValue {
  readonly completedAt?: string;
  readonly passedAt?: string;
}

/**
 * What the pure rules need to know about the curriculum, and nothing more.
 * `@proef/curriculum`'s derived manifest satisfies it structurally; it lists
 * published lessons only, because a draft never reaches a device.
 */
export interface LearnManifestStep {
  readonly id: number;
  readonly kind: StepKind;
}

export interface LearnManifestLesson {
  readonly level: number;
  readonly module: string;
  readonly steps: readonly LearnManifestStep[];
  readonly checks: readonly string[];
  readonly drillItems: readonly string[];
}

export interface LearnManifestModule {
  readonly id: string;
  readonly lessons: readonly string[];
}

export interface LearnManifestLevel {
  readonly id: number;
  readonly modules: readonly LearnManifestModule[];
}

export interface LearnManifestDrillItem {
  readonly id: string;
  readonly kind: DrillKind;
  readonly lessonId: string;
  readonly stepId: number;
  /** Position across the whole curriculum; new items are introduced in this order. */
  readonly order: number;
}

export interface LearnManifest {
  readonly levels: readonly LearnManifestLevel[];
  readonly lessons: Readonly<Record<string, LearnManifestLesson>>;
  readonly drillItems: readonly LearnManifestDrillItem[];
}
